/**
 * @fileoverview Base schemas, constants, and validators shared between frontend
 * and backend to ensure perfect symmetry in data validation.
 */

import { z } from 'zod';
import { ID_PATTERNS } from './types.js';

// =============================================================================
// Reusable Schema Fragments
// =============================================================================

/**
 * Creates an optional nullable datetime schema that transforms empty strings to null.
 */
export function createOptionalDatetimeSchema(fieldName: string) {
  return z.string()
    .transform((val): string | null => (val === '' ? null : val))
    .pipe(
      z.string()
        .datetime(`${fieldName} must be a valid ISO datetime`)
        .nullable()
    )
    .optional()
    .nullable();
}

/**
 * Creates an optional datetime schema that transforms empty strings to undefined.
 */
function createTimestampSchema(fieldName: string): z.ZodOptional<z.ZodPipeline<z.ZodEffects<z.ZodString, string | undefined, string>, z.ZodString>> {
  return z.string()
    .transform((val): string | undefined => (val === '' ? undefined : val))
    .pipe(
      z.string()
        .datetime(`${fieldName} must be a valid ISO datetime`)
    )
    .optional();
}

// =============================================================================
// Pre-built Common Schemas
// =============================================================================

/**
 * Standard deleted_at schema - nullable datetime
 */
export const deletedAtSchema = createOptionalDatetimeSchema('Deleted at');

/**
 * Standard archived_at schema - nullable datetime
 */
export const archivedAtSchema = createOptionalDatetimeSchema('Archived at');

/**
 * Standard created_at schema - optional datetime
 */
export const createdAtSchema = createTimestampSchema('Created at');

/**
 * Standard updated_at schema - optional datetime
 */
export const updatedAtSchema = createTimestampSchema('Updated at');

// =============================================================================
// Entity ID Schema Factory
// =============================================================================

/**
 * Creates an entity-specific ID schema with prefix validation.
 * Transforms empty strings to undefined and validates against the provided pattern.
 *
 * @param pattern - Regex pattern for the ID format (e.g., /^ca[2-9][a-z0-9]{4}$/)
 * @param errorMessage - Error message shown when validation fails
 * @returns Zod schema for the entity ID
 *
 * @example
 * ```ts
 * const categoryIdSchema = createEntityIdSchema(
 *   /^ca[2-9][abcdefghijkmnpqrstuvwxyz23456789]{4}$/,
 *   'ID must be in ca-prefixed nanoid7 format'
 * );
 * ```
 */
export function createEntityIdSchema(pattern: RegExp, errorMessage: string) {
  return z.string()
    .transform((val) => (val === '' ? undefined : val))
    .pipe(z.string().regex(pattern, errorMessage))
    .optional();
}

// =============================================================================
// Common Validators
// =============================================================================

/**
 * Common validators used across multiple schemas. Return types are inferred
 * by TypeScript to avoid complex explicit type annotations.
 */
export const commonValidators = {
  /**
   * Required string with length constraints
   */
  requiredString: (fieldName: string, maxLength: number = 255) =>
    z.string()
      .min(1, `${fieldName} is required`)
      .max(maxLength, `${fieldName} must be ${maxLength} characters or less`)
      .trim(),

  /**
   * Optional string with length constraints
   */
  optionalString: (maxLength: number = 255) =>
    z.string()
      .trim()
      .transform((val): string | null => (val === '' ? null : val))
      .pipe(
        z.string()
          .max(maxLength, `Must be ${maxLength} characters or less`)
          .nullable()
      )
      .optional()
      .nullable(),

  /**
   * Email validation
   */
  email: () =>
    z.string()
      .transform((val): string | null => (val === '' ? null : val))
      .pipe(
        z.string()
          .email('Must be a valid email address')
          .nullable()
      )
      .optional()
      .nullable(),

  /**
   * Phone validation (flexible format)
   */
  phone: () =>
    z.string()
      .transform((val): string | null => (val === '' ? null : val))
      .pipe(
        z.string()
          .regex(/^\+?[\d\s\-()]+$/, 'Must be a valid phone number')
          .nullable()
      )
      .optional()
      .nullable(),

  /**
   * Foreign key validation (8-char nanoid format: 2-letter prefix + 6 alphanumeric)
   * Accepts: valid nanoid8 (e.g., 'pe123abc'), null, undefined, or empty string (converted to null)
   */
  foreignKey: (fieldName: string = 'ID') =>
    z.string()
      .transform((val): string | null => (val === '' ? null : val))
      .pipe(
        z.string()
          .regex(ID_PATTERNS.FOREIGN_KEY, `${fieldName} must be a valid ID (format: 2 letters + 5-6 alphanumeric)`)
          .nullable()
      )
      .optional()
      .nullable(),

  /**
   * JSON field validation (accepts null or valid JSON string)
   */
  jsonField: (fieldName: string = 'JSON') =>
    z.string()
      .refine(
        (val): boolean => {
          if (!val || val === null) return true;
          try {
            JSON.parse(val);
            return true;
          } catch {
            return false;
          }
        },
        { message: `${fieldName} must be valid JSON or null` }
      )
      .optional()
      .nullable(),

  /**
   * Boolean with default
   */
  booleanWithDefault: (defaultValue: boolean = false) =>
    z.boolean().default(defaultValue),

  /**
   * Positive integer
   */
  positiveInteger: (fieldName: string = 'Value') =>
    z.number()
      .int(`${fieldName} must be an integer`)
      .positive(`${fieldName} must be positive`),

  /**
   * Date string (YYYY-MM-DD format)
   */
  dateString: () =>
    z.string()
      .transform((val): string | null => (val === '' ? null : val))
      .pipe(
        z.string()
          .regex(/^\d{4}-\d{2}-\d{2}$/, 'Must be in YYYY-MM-DD format')
          .nullable()
      )
      .optional()
      .nullable(),

  /**
   * Optional URL (transforms empty string to null)
   */
  optionalUrl: (fieldName: string = 'URL') =>
    z.string()
      .transform((val): string | null => (val === '' ? null : val))
      .pipe(
        z.string()
          .url(`${fieldName} must be a valid URL`)
          .nullable()
      )
      .optional()
      .nullable(),

  /**
   * Note foreign key validation (nanoid5 format: digit 2-9 + 4 alphanumeric).
   * Note IDs don't use the standard 2-letter prefix, so they need a dedicated
   * validator instead of the generic foreignKey().
   */
  noteForeignKey: (fieldName: string = 'Note ID') =>
    z.string()
      .transform((val): string | null => (val === '' ? null : val))
      .pipe(
        z.string()
          .regex(ID_PATTERNS.NOTE, `${fieldName} must be in nanoid5 format`)
          .nullable()
      )
      .optional()
      .nullable(),

  /**
   * Display order (integer >= 1, defaults to 1)
   */
  displayOrder: (defaultValue: number = 1) =>
    z.number()
      .int('Display order must be an integer')
      .min(1, 'Display order must be at least 1')
      .default(defaultValue),

  /**
   * ISO 3166-1 alpha-2 country code (2 uppercase letters)
   */
  countryCode: () =>
    z.string()
      .transform((val): string | null => (val === '' ? null : val))
      .pipe(
        z.string()
          .length(2, 'Country code must be 2 characters')
          .regex(/^[A-Z]{2}$/, 'Country code must be 2 uppercase letters')
          .nullable()
      )
      .optional()
      .nullable(),
} as const;

// =============================================================================
// Account Tracking Field Groups
// =============================================================================

/**
 * Standard account tracking fields for entities that record who created,
 * updated, and deleted them. Spread into z.object() or additionalFields.
 */
export const accountTrackingFields = {
  created_by_account_id: commonValidators.foreignKey('Created by account ID'),
  updated_by_account_id: commonValidators.foreignKey('Updated by account ID'),
  deleted_by_account_id: commonValidators.foreignKey('Deleted by account ID'),
} as const;

/**
 * Extended account tracking fields for archivable entities. Includes
 * archived_by_account_id in addition to the standard tracking fields.
 */
export const archivableAccountTrackingFields = {
  ...accountTrackingFields,
  archived_by_account_id: commonValidators.foreignKey('Archived by account ID'),
} as const;

// =============================================================================
// CRUD Schema Factory
// =============================================================================

/**
 * Standard fields omitted from Create schemas
 */
const BASE_OMIT_FIELDS = {
  id: true,
  created_at: true,
  updated_at: true,
} as const;

/**
 * Fields commonly omitted from Create schemas for soft-deletable entities
 */
const SOFT_DELETE_OMIT_FIELDS = {
  ...BASE_OMIT_FIELDS,
  deleted_at: true,
} as const;

/** Keys always omitted from Create schemas (id, created_at, updated_at). */
type BaseOmitKeys = keyof typeof BASE_OMIT_FIELDS;

/**
 * The precise union of keys removed from a base schema's shape when building
 * its Create schema. Combines the always-omitted base fields, the optional
 * `deleted_at`, and any caller-supplied `additionalOmit` fields, intersected
 * with the schema's actual keys so the mask never references absent fields
 * (e.g. schemas without `updated_at`/`deleted_at`).
 */
type CrudOmitKeys<
  T extends z.AnyZodObject,
  TOmit extends readonly PropertyKey[],
  TOmitDeletedAt extends boolean,
> = (
  | BaseOmitKeys
  | (TOmitDeletedAt extends true ? 'deleted_at' : never)
  | TOmit[number]
) &
  keyof T['shape'];

/** The Create schema produced by {@link createCrudSchemas} for the given inputs. */
type CrudCreateSchema<
  T extends z.AnyZodObject,
  TOmit extends readonly PropertyKey[],
  TOmitDeletedAt extends boolean,
> = z.ZodObject<Omit<T['shape'], CrudOmitKeys<T, TOmit, TOmitDeletedAt>>>;

/**
 * Helper to create Create and Update schemas from a base schema.
 * Reduces boilerplate by automatically omitting base fields for Create
 * and making all fields partial for Update.
 *
 * The omit mask is tracked precisely at the type level: `additionalOmit` is a
 * `const` tuple and `omitDeletedAt` flows through as a literal, so the derived
 * Create/Update types accurately reflect the remaining fields (rather than
 * collapsing to a permissive index-signature object).
 *
 * @param baseSchema - The full entity schema
 * @param options - Configuration options
 * @param options.omitDeletedAt - Whether to omit deleted_at from Create schema (default: true)
 * @param options.additionalOmit - Additional fields to omit from Create schema
 * @returns Object containing CreateSchema and UpdateSchema
 *
 * @example
 * ```ts
 * // Standard entity (deleted_at omitted by default)
 * const { CreateSchema, UpdateSchema } = createCrudSchemas(PersonSchema);
 *
 * // Custom omissions
 * const { CreateSchema, UpdateSchema } = createCrudSchemas(WeightSchema, {
 *   additionalOmit: ['weight_kg', 'weight_st'] // Generated columns
 * });
 * ```
 */
export function createCrudSchemas<
  T extends z.AnyZodObject,
  const TOmit extends readonly (keyof T['shape'])[] = readonly [],
  const TOmitDeletedAt extends boolean = true,
>(
  baseSchema: T,
  options: {
    omitDeletedAt?: TOmitDeletedAt;
    additionalOmit?: TOmit;
  } = {}
) {
  const { omitDeletedAt = true, additionalOmit = [] } = options;

  // Build omit object
  const omitFields: Record<string, true> = omitDeletedAt
    ? { ...SOFT_DELETE_OMIT_FIELDS }
    : { ...BASE_OMIT_FIELDS };

  for (const field of additionalOmit) {
    omitFields[field as string] = true;
  }

  // The runtime mask is built dynamically (loop + conditional), which TypeScript
  // can't track as a literal type, so we re-assert the precise Create shape here.
  // CrudOmitKeys mirrors exactly what `omitFields` removes at runtime.
  const CreateSchema = baseSchema.omit(omitFields) as unknown as CrudCreateSchema<
    T,
    TOmit,
    TOmitDeletedAt
  >;
  const UpdateSchema = CreateSchema.partial();

  return {
    CreateSchema,
    UpdateSchema,
  };
}

// =============================================================================
// Displayable Entity Schema Factory
// =============================================================================

/**
 * Base fields for all displayable lookup entities (categories, kinds, lists, etc.)
 */
const displayableBaseFields = {
  name: commonValidators.requiredString('Name', 50),
  description: commonValidators.optionalString(1000),
  display_order: commonValidators.displayOrder(),
  metadata: commonValidators.jsonField('Metadata'),
  deleted_at: deletedAtSchema,
  created_at: createdAtSchema,
  updated_at: updatedAtSchema,
} as const;

/**
 * Configuration options for displayable entity schemas.
 */
interface DisplayableEntityOptions {
  /** Whether the entity supports archiving (adds archived_at field) */
  archivable?: boolean;
  /** Account tracking level: 'standard' (created/updated/deleted_by) or 'archivable' (+ archived_by) */
  accountTracking?: 'standard' | 'archivable';
  /** Additional Zod fields to merge into the schema */
  additionalFields?: z.ZodRawShape;
}

/**
 * Creates a complete displayable entity schema with standardized fields.
 * Use for lookup/reference entities like Category, Kind, List, Status, etc.
 *
 * @param idPattern - Regex pattern for the entity ID
 * @param idErrorMessage - Error message for invalid IDs
 * @param options - Optional configuration for archivable and additional fields
 * @returns Complete Zod object schema for the entity
 *
 * @example
 * ```ts
 * // Simple lookup entity
 * export const KindSchema = createDisplayableEntitySchema(
 *   ID_PATTERNS.KIND,
 *   'ID must be in ki-prefixed nanoid7 format'
 * );
 *
 * // Archivable entity
 * export const AreaSchema = createDisplayableEntitySchema(
 *   ID_PATTERNS.AREA,
 *   'ID must be in ar-prefixed nanoid7 format',
 *   { archivable: true }
 * );
 *
 * // Entity with extra fields
 * export const StatusSchema = createDisplayableEntitySchema(
 *   ID_PATTERNS.STATUS,
 *   'ID must be in st-prefixed nanoid7 format',
 *   { additionalFields: { status_category: StatusCategoryEnum.default('new') } }
 * );
 * ```
 */
export function createDisplayableEntitySchema(
  idPattern: RegExp,
  idErrorMessage: string,
  options: DisplayableEntityOptions = {}
): z.ZodObject<z.ZodRawShape> {
  const { archivable = false, accountTracking, additionalFields = {} } = options;

  const trackingFields = accountTracking === 'archivable'
    ? archivableAccountTrackingFields
    : accountTracking === 'standard'
      ? accountTrackingFields
      : {};

  const fields: z.ZodRawShape = {
    id: createEntityIdSchema(idPattern, idErrorMessage),
    ...displayableBaseFields,
    ...(archivable ? { archived_at: archivedAtSchema } : {}),
    ...trackingFields,
    ...additionalFields,
  };

  return z.object(fields);
}

// =============================================================================
// Schema Field Extraction Utility
// =============================================================================

/**
 * Extracts field names from a Zod object schema as a readonly tuple.
 * Eliminates need for manually maintaining separate FIELDS arrays.
 *
 * @param schema - Zod object schema to extract fields from
 * @returns Readonly array of field names
 *
 * @example
 * ```ts
 * export const TAG_FIELDS = getSchemaFields(TagSchema);
 * // Type: readonly ["id", "name", "description", ...]
 * ```
 */
export function getSchemaFields<T extends z.ZodObject<z.ZodRawShape>>(
  schema: T
): readonly (keyof z.infer<T> & string)[] {
  return Object.keys(schema.shape) as (keyof z.infer<T> & string)[];
}
